/**
 * Editor state and transitions.
 *
 * Follows the handoff's "State Management" section, with one deliberate change: the board is
 * held as an `ImageRect` rather than stage-pixel `x/y/size` (ADR-0011), so it survives any
 * stage change without migration.
 *
 * Nothing here is persisted (ADR-0007) — a reload starts from defaults.
 */

import type { ImageRect, Size } from '../../lib/geometry';
import type { Sheets } from '../../lib/export';
import { DEFAULT_LINE } from '../../lib/draw';

/** A loaded picture, ready to draw from. */
export interface Picture {
  /** What the `<img>` was pointed at — a proxy URL or an object URL. */
  readonly src: string;
  /** The picture's native dimensions. */
  readonly natural: Size;
  /** The decoded element, reused for export so it is never fetched twice. */
  readonly element: HTMLImageElement;
}

export interface EditorState {
  /** Text in the URL field. */
  readonly url: string;
  /** Whether a load is in flight. */
  readonly loading: boolean;
  readonly picture: Picture | null;
  /** Board rect in image px. Set together with `picture`, cleared together with it. */
  readonly board: ImageRect | null;
  /** Gridline and ruler colour. */
  readonly line: string;
  /** Ruler type scale, 0.7–2.4. */
  readonly labelScale: number;
  /** True while the (synchronous) export is being prepared — see ADR-0009. */
  readonly generating: boolean;
  readonly result: Sheets | null;
  /** Which sheet the mobile export view is showing. */
  readonly tab: keyof Sheets;
  readonly error: string | null;
}

export const initialState: EditorState = {
  url: '',
  loading: false,
  picture: null,
  board: null,
  line: DEFAULT_LINE,
  labelScale: 1,
  generating: false,
  result: null,
  tab: 'quiz',
  error: null,
};

export type Action =
  | { type: 'urlChanged'; url: string }
  | { type: 'loadStarted' }
  | { type: 'loaded'; picture: Picture; board: ImageRect }
  | { type: 'failed'; message: string }
  | { type: 'boardMoved'; board: ImageRect }
  | { type: 'lineChanged'; line: string }
  | { type: 'labelScaleChanged'; labelScale: number }
  | { type: 'generateStarted' }
  | { type: 'generated'; result: Sheets }
  | { type: 'resultClosed' }
  | { type: 'tabChanged'; tab: keyof Sheets }
  | { type: 'errorDismissed' }
  | { type: 'reset' };

export function reducer(state: EditorState, action: Action): EditorState {
  switch (action.type) {
    case 'urlChanged':
      return { ...state, url: action.url };

    case 'loadStarted':
      return { ...state, loading: true, error: null };

    case 'loaded':
      return {
        ...state,
        loading: false,
        picture: action.picture,
        board: action.board,
        result: null,
        error: null,
      };

    case 'failed':
      return { ...state, loading: false, generating: false, error: action.message };

    case 'boardMoved':
      return state.picture === null ? state : { ...state, board: action.board };

    case 'lineChanged':
      return { ...state, line: action.line };

    case 'labelScaleChanged':
      return { ...state, labelScale: action.labelScale };

    case 'generateStarted':
      return { ...state, generating: true, error: null };

    case 'generated':
      return { ...state, generating: false, result: action.result, tab: 'quiz', error: null };

    case 'resultClosed':
      return { ...state, result: null };

    case 'tabChanged':
      return { ...state, tab: action.tab };

    case 'errorDismissed':
      return { ...state, error: null };

    case 'reset':
      // Start over clears the picture and its board but keeps the colour and label
      // preferences, exactly as the handoff specifies.
      return {
        ...initialState,
        line: state.line,
        labelScale: state.labelScale,
      };

    default: {
      const exhaustive: never = action;
      return exhaustive;
    }
  }
}
